/**
 * Floating Support / Suggestion button — mirrors the phone-button position
 * but lives on the BOTTOM RIGHT. Anyone (anonymous or logged-in) can submit
 * a message that lands in the master admin's Support Inbox.
 */
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { LifeBuoy, X, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const HIDDEN_ROUTES = ['/pay/i', '/portal/c'];  // public pay/portal pages — keep them clean

export default function SupportButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('support');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p))) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 5) return toast.error('Message is too short');
    setBusy(true);
    try {
      // withCredentials so the anonymous mkb_visitor cookie flows up
      await axios.post(
        `${API}/api/support/message`,
        {
          kind,
          subject: subject || null,
          message,
          name: name || null,
          email: email || null,
          page_url: window.location.href.slice(0, 500),
        },
        { withCredentials: true },
      );
      setSent(true);
      toast.success('Thanks — message received!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — try again');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setSent(false);
      setSubject('');
      setMessage('');
      setKind('support');
    }, 250);
  };

  return (
    <>
      {/* Floating trigger — top right (next to Phone) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-[60] w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-indigo-500 text-white shadow-lg hover:scale-105 transition-transform flex items-center justify-center"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 64px)',
        }}
        aria-label="Support & suggestions"
        data-testid="support-button"
      >
        <LifeBuoy className="w-5 h-5" />
      </button>

      {/* Popover */}
      {open && (
        <div
          className="fixed z-[60] w-[340px] max-w-[calc(100vw-2rem)] bg-card border rounded-xl shadow-2xl overflow-hidden"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
            right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
            maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 100px)',
            overflowY: 'auto',
          }}
          data-testid="support-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-sky-500/10 to-indigo-500/10">
            <div className="flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-sky-400" />
              <span className="font-semibold">Support & Feedback</span>
            </div>
            <button onClick={reset} aria-label="Close" className="p-1 hover:bg-muted rounded">
              <X className="w-4 h-4" />
            </button>
          </div>

          {sent ? (
            <div className="p-6 text-center space-y-3">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto" />
              <p className="font-semibold">Got it — thank you!</p>
              <p className="text-xs text-muted-foreground">
                We've received your message and will get back to you soon.
              </p>
              <button
                onClick={reset}
                className="w-full py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Type</label>
                <div className="grid grid-cols-4 gap-1 mt-1">
                  {[
                    { id: 'support', label: 'Help' },
                    { id: 'suggestion', label: 'Idea' },
                    { id: 'bug', label: 'Bug' },
                    { id: 'other', label: 'Other' },
                  ].map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setKind(o.id)}
                      className={`text-xs py-1.5 rounded-md border transition ${
                        kind === o.id
                          ? 'bg-sky-500/15 border-sky-500/50 text-sky-200 font-semibold'
                          : 'border-input hover:bg-muted text-muted-foreground'
                      }`}
                      data-testid={`support-kind-${o.id}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                placeholder="Subject (optional)"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="support-subject"
              />
              <textarea
                placeholder="Tell us what's on your mind..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={5000}
                className="w-full py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                data-testid="support-message"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  className="py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="py-2 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full py-2.5 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 hover:opacity-90 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-60"
                data-testid="support-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
              <p className="text-[10px] text-muted-foreground text-center">
                Replies go directly to the My Key Boss team.
              </p>
            </form>
          )}
        </div>
      )}
    </>
  );
}
